import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ColorPicker } from '@/components/design/ColorPicker';
import { EmailPreview } from '@/components/design/EmailPreview';
import { PlaceholderMenu } from '@/components/design/PlaceholderMenu';
import { fieldsFor, FIELD_META, templateLabel, TEMPLATE_LABELS, type ContentField } from '@/components/design/emailTemplateMeta';
import { EmailTemplate, emailTemplateService } from '@/services/emailTemplateService';
import { useToast } from '@/hooks/use-toast';
import { Save, RotateCcw, Copy, Trash2, Power, ArrowLeft, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface EmailTemplateEditorProps {
  template: EmailTemplate | null;
  onSave: (template: EmailTemplate) => void;
  onCancel: () => void;
  onDelete?: (templateId: string) => void;
  onSetActive?: (templateId: string, templateType: string) => void;
  onDuplicate?: (template: EmailTemplate) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const DEFAULT_COLORS = {
  primary_color: '188 100% 35%',
  secondary_color: '16 85% 64%',
  background_color: '0 0% 100%',
  text_color: '0 0% 20%',
  button_color: '188 100% 35%',
  button_text_color: '0 0% 100%',
};
const BRAND_COLORS = {
  primary_color: '335 41% 14%',
  secondary_color: '71 73% 77%',
  background_color: '30 23% 83%',
  text_color: '335 41% 14%',
  button_color: '335 41% 14%',
  button_text_color: '0 0% 100%',
};

export function EmailTemplateEditor({
  template, onSave, onCancel, onDelete, onSetActive, onDuplicate, onDirtyChange,
}: EmailTemplateEditorProps) {
  const { toast } = useToast();
  const [editingTemplate, setEditingTemplate] = useState<Partial<EmailTemplate> | null>(null);
  const [dirty, setDirtyState] = useState(false);
  const isNew = !template?.id;

  const setDirty = (d: boolean) => { setDirtyState(d); onDirtyChange?.(d); };

  const reset = () => {
    if (template) setEditingTemplate({ ...template, color_config: { ...DEFAULT_COLORS, ...(template.color_config || {}) } as any });
    else setEditingTemplate({
      template_type: 'user_invitation', name: 'New email template', subject_template: 'Welcome to {{organizationName}}',
      content_config: { greeting: 'Hi {{contactName}},', cta_text: 'Get started' },
      color_config: { ...DEFAULT_COLORS }, is_active: false,
    });
    setDirty(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reset, [template]);

  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  if (!editingTemplate) return null;
  const type = editingTemplate.template_type || 'user_invitation';
  const content = (editingTemplate.content_config || {}) as Record<string, any>;
  const variables = emailTemplateService.getAvailableVariables(type);

  const patch = (p: Partial<EmailTemplate>) => { setEditingTemplate((t) => ({ ...t!, ...p })); setDirty(true); };
  const updateContent = (key: string, value: any) =>
    patch({ content_config: { ...(editingTemplate.content_config || {}), [key]: value } as any });
  const updateColor = (key: string, value: string) =>
    patch({ color_config: { ...(editingTemplate.color_config || {}), [key]: value } as any });

  const handleSave = () => {
    if (!editingTemplate.name?.trim()) { toast({ description: 'Give the template a name first', variant: 'destructive' }); return; }
    onSave(editingTemplate as EmailTemplate);
    setDirty(false);
  };
  const handleCancel = () => {
    if (dirty && !confirm('Discard your unsaved changes?')) return;
    setDirty(false);
    onCancel();
  };
  const handleDelete = () => {
    if (!template?.id || !onDelete) return;
    if (confirm(`Delete "${template.name}"? This can't be undone.`)) onDelete(template.id);
  };
  const handleSetActive = () => {
    if (!template?.id || !onSetActive) return;
    if (dirty) { toast({ description: 'Save your changes before making this version live', variant: 'destructive' }); return; }
    onSetActive(template.id, template.template_type);
  };
  const sendPartnerTest = async () => {
    const res = await supabase.functions.invoke('send-partner-invitation', {
      body: { partnerName: 'Test Partner', partnerEmail: 'test@example.com', testEmail: true, dryRun: false },
    });
    if (res.error) toast({ title: 'Test email failed', description: res.error.message, variant: 'destructive' });
    else toast({ title: 'Test email sent', description: 'Check your inbox for the test email' });
  };

  const renderField = (f: ContentField) => {
    const meta = FIELD_META[f];
    const id = `field-${f}`;
    if (f === 'show_image') {
      return (
        <div key={f} className="flex items-center justify-between rounded-md border p-3">
          <Label htmlFor={id}>{meta.label}</Label>
          <Switch id={id} checked={content.show_image !== false} onCheckedChange={(v) => updateContent('show_image', v)} />
        </div>
      );
    }
    const value = f === 'benefits' ? (Array.isArray(content.benefits) ? content.benefits.join('\n') : '') : (content[f] || '');
    const set = (v: string) => updateContent(f, f === 'benefits' ? v.split('\n').filter((l) => l.trim()) : v);
    return (
      <div key={f} className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor={id}>{meta.label}</Label>
          {f !== 'benefits' && <PlaceholderMenu variables={variables} targetId={id} value={value} onChange={set} />}
        </div>
        {meta.multiline
          ? <Textarea id={id} value={value} onChange={(e) => set(e.target.value)} placeholder={meta.placeholder} rows={f === 'benefits' ? 4 : 3} />
          : <Input id={id} value={value} onChange={(e) => set(e.target.value)} placeholder={f === 'badge_text' && type === 'camera_image' ? 'NEW IMAGE' : meta.placeholder} />}
      </div>
    );
  };

  const isCamera = type === 'camera_alert' || type === 'camera_image';
  const colors = { ...DEFAULT_COLORS, ...(editingTemplate.color_config || {}) } as Record<string, string>;

  return (
    <div className="space-y-4">
      {/* Sticky save bar */}
      <div className="sticky top-0 z-10 -mx-1 px-1 py-2 bg-background/95 backdrop-blur border-b flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={handleCancel} aria-label="Back to list">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h3 className="font-semibold truncate">{editingTemplate.name || 'Untitled template'}</h3>
            <p className="text-xs text-muted-foreground">{templateLabel(type)}</p>
          </div>
          {template?.is_active && <Badge>Live</Badge>}
          {isNew && <Badge variant="outline">New</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          {dirty && <Button variant="outline" size="sm" onClick={reset}><RotateCcw className="h-4 w-4 mr-1" />Discard</Button>}
          <Button size="sm" onClick={handleSave} disabled={!dirty && !isNew}><Save className="h-4 w-4 mr-1" />Save</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
        <Tabs defaultValue="content" className="space-y-4">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="style">Style</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="content">
            <Card><CardContent className="pt-6 space-y-4">
              {isCamera && (
                <p className="text-sm text-muted-foreground">
                  The heading and description come from the camera's AI result. Partner brands can override this wording in Brand Studio.
                </p>
              )}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="field-subject">Subject line</Label>
                  <PlaceholderMenu variables={variables} targetId="field-subject" value={editingTemplate.subject_template || ''} onChange={(v) => patch({ subject_template: v })} />
                </div>
                <Input id="field-subject" value={editingTemplate.subject_template || ''} onChange={(e) => patch({ subject_template: e.target.value })} />
              </div>
              {fieldsFor(type, content).map(renderField)}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="style">
            <Card><CardContent className="pt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="field-logo">Logo URL</Label>
                <Input id="field-logo" value={editingTemplate.logo_url || ''} onChange={(e) => patch({ logo_url: e.target.value })} placeholder="https://example.com/logo.png" />
              </div>
              {isCamera && <p className="text-xs text-muted-foreground">Camera emails use the brand's colours and fonts when sent to partner users.</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ColorPicker label="Primary" value={colors.primary_color} onChange={(v) => updateColor('primary_color', v)} />
                <ColorPicker label="Secondary" value={colors.secondary_color} onChange={(v) => updateColor('secondary_color', v)} />
                <ColorPicker label="Background" value={colors.background_color} onChange={(v) => updateColor('background_color', v)} />
                <ColorPicker label="Text" value={colors.text_color} onChange={(v) => updateColor('text_color', v)} />
                <ColorPicker label="Button" value={colors.button_color} onChange={(v) => updateColor('button_color', v)} />
                <ColorPicker label="Button text" value={colors.button_text_color} onChange={(v) => updateColor('button_text_color', v)} />
                {isCamera && <>
                  <ColorPicker label="Badge background" value={content.badge_background || colors.secondary_color} onChange={(v) => updateContent('badge_background', v)} />
                  <ColorPicker label="Badge text colour" value={content.badge_text_color || colors.button_text_color} onChange={(v) => updateContent('badge_text_color', v)} />
                </>}
              </div>
              <Button variant="outline" size="sm" onClick={() => patch({ color_config: { ...BRAND_COLORS } as any })}>
                <RotateCcw className="h-4 w-4 mr-1" />Reset to Snapshot brand colours
              </Button>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card><CardContent className="pt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="field-name">Template name</Label>
                <Input id="field-name" value={editingTemplate.name || ''} onChange={(e) => patch({ name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="field-type">Email type</Label>
                <select
                  id="field-type"
                  className="w-full h-10 px-3 border border-input rounded-md bg-background text-foreground text-sm disabled:opacity-60"
                  value={type}
                  onChange={(e) => patch({ template_type: e.target.value as EmailTemplate['template_type'] })}
                  disabled={!isNew}
                >
                  {Object.entries(TEMPLATE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                {!isNew && <p className="text-xs text-muted-foreground">The type can't change after saving.</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="field-from">Sender (From)</Label>
                <Input id="field-from" value={(editingTemplate as any).from_address || ''} onChange={(e) => patch({ from_address: e.target.value } as any)} placeholder="Name <email@example.com>" />
              </div>

              {!isNew && (
                <div className="rounded-md border p-3 space-y-2">
                  <div className="text-sm font-medium">Live version</div>
                  {template?.is_active ? (
                    <p className="text-sm text-muted-foreground">This is the version sent for "{templateLabel(type)}" emails.</p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">Making this live replaces the current live version of "{templateLabel(type)}".</p>
                      {onSetActive && <Button variant="outline" size="sm" onClick={handleSetActive}><Power className="h-4 w-4 mr-1" />Set as live</Button>}
                    </>
                  )}
                </div>
              )}

              {!isNew && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {onDuplicate && (
                    <Button variant="outline" size="sm" onClick={() => onDuplicate({ ...(editingTemplate as EmailTemplate) })}>
                      <Copy className="h-4 w-4 mr-1" />Duplicate
                    </Button>
                  )}
                  {type === 'partner_invitation' && (
                    <Button variant="outline" size="sm" onClick={sendPartnerTest}><Send className="h-4 w-4 mr-1" />Send test email</Button>
                  )}
                  {onDelete && (
                    <Button variant="destructive" size="sm" onClick={handleDelete} disabled={template?.is_active} title={template?.is_active ? 'Make another version live before deleting' : undefined}>
                      <Trash2 className="h-4 w-4 mr-1" />Delete
                    </Button>
                  )}
                </div>
              )}
            </CardContent></Card>
          </TabsContent>
        </Tabs>

        <div className="xl:sticky xl:top-16 self-start">
          <EmailPreview template={editingTemplate as EmailTemplate} />
        </div>
      </div>
    </div>
  );
}
